import { API_DOMAIN } from 'consts';
import { security } from 'shared';

// In-flight request counter consumed by PrintArtifactPage's readiness signal.
// The TradingView datafeed fetches go through this helper, NOT react-query, so
// `useIsFetching()` alone can't tell when the chart is done loading. We expose
// this counter on `window` so the print page can wait for it to drain before
// signaling __artifactReady to the headless screenshotter.
declare global {
  interface Window {
    __pendingDatafeedRequests?: number;
  }
}

const incrementPending = (): void => {
  if (typeof window === 'undefined') return;
  window.__pendingDatafeedRequests = (window.__pendingDatafeedRequests ?? 0) + 1;
};
const decrementPending = (): void => {
  if (typeof window === 'undefined') return;
  window.__pendingDatafeedRequests = Math.max(0, (window.__pendingDatafeedRequests ?? 0) - 1);
};

export async function makeApiRequest (path: string): Promise<any> {
  incrementPending();
  try {
    // Print mode: when running inside the headless renderer, there is no
    // Auth0 session — `security.getAccessTokenSilently` is never wired up
    // (PrintArtifactPage bypasses PageLayout, which is what registers it).
    // PrintArtifactPage instead seeds `window.__renderToken` from the
    // `?token=` query param, mirroring how `OilyticsClient.create()` picks
    // it up. Calling `getAccessTokenSilently()()` without that fallback
    // throws "is not a function" and blanks out the chart, so we check
    // here first. Order matches OilyticsClient: render token wins.
    const renderToken = (window as unknown as { __renderToken?: string }).__renderToken;
    const token = renderToken ?? await security.getAccessTokenSilently()();
    // Ensure path starts with / for absolute URL when API_DOMAIN is empty
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const url = API_DOMAIN ? `${API_DOMAIN}${normalizedPath}` : normalizedPath;
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  } finally {
    decrementPending();
  }
}

export function generateSymbol (exchange: string, fromSymbol: string, toSymbol: string) {
  const short = `${fromSymbol}/${toSymbol}`;
  return {
    short,
    full: `${exchange}:${short}`,
  };
}

export function parseFullSymbol (fullSymbol: string) {
  const match = fullSymbol.match(/^(\w+):(\w+)\/(\w+)$/);
  if (!match) {
    throw new Error(`Invalid symbol format: ${fullSymbol}`);
  }
  return {
    exchange: match[1],
    fromSymbol: match[2],
    toSymbol: match[3],
  };
}
